import { Map as ImmutableMap } from 'immutable';
import { v4 as uuid } from 'uuid';

import { selectShouldHideBusinessFromAiChat } from 'app-components/flux/ai-chat/aiChatSelectors';
import { getBusinessCoaIdsSet, getCategoriesById } from 'app-components/flux/categories/categoriesSelectors';
import type { ChartOfAccountProps } from 'app-components/flux/chart-of-accounts/chartOfAccountsTypes';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { createDialog } from 'app-components/flux/root-ui/rootUiActions';
import { mkRootUiData } from 'app-components/flux/root-ui/rootUiTypes';
import * as transactionsSelectors from 'app-components/flux/transactions/transactionsSelectors';
import { isInvoiceRelatedTransaction } from 'app-components/flux/transactions/transactionsUtils';
import { getLogger } from 'app-components/utils/core';
import tracker from 'app-components/utils/core/tracker';
import store from 'app-components/utils/redux-store';
import type RootState from 'app-components/utils/redux-store/rootState';

import { safeParse } from '../../../aiChatUtils';
import { resolveBusinessFieldsForCategory } from '../../businessAssignment';
import { returnToolResponse } from '../../llmToolsFramework';
import type { LlmToolsCategoryDict, ToolMessage } from '../../llmToolsTypes';
import {
  categoriesForAI,
  isBusinessTransaction,
  isEstimatedTransaction,
  isInvestmentTransaction,
  isSplitTransaction,
} from '../../utils';

const log = getLogger('llmCategoryTools');
const DEFAULT_MAX_CATEGORIZATION_TRANSACTIONS = 50;

const transactionMappingsCache = new Map<string, any[]>();

const categoryListCache = new Map<string, any>();

type Step1Mapping = {
  id: string;
  payee: string;
  date: string;
  category: string;
};

type Step2Mapping = {
  id: string;
  categoryName?: string;
  categoryId?: string;
  payee?: string;
  originalPayee?: string;
};

type SkipReason = 'split' | 'investment' | 'estimated' | 'invoice' | 'business' | 'not_found';

const SKIP_REASON_MESSAGES: Record<SkipReason, string> = {
  split: "it's a split transaction and must be edited directly in the app",
  investment: "it's an investment transaction",
  estimated: "it's an estimated transaction",
  invoice: "it's an invoice-related transaction",
  business: "it's a business transaction",
  not_found: 'its ID could not be found (it may have changed since STEP1)',
};

const describeSkippedTransactions = (reasons: SkipReason[]): string => {
  if (reasons.length === 0) return '';

  const counts = reasons.reduce<Partial<Record<SkipReason, number>>>((acc, reason) => {
    acc[reason] = (acc[reason] || 0) + 1;
    return acc;
  }, {});

  const parts = (Object.entries(counts) as Array<[SkipReason, number]>).map(
    ([reason, count]) => `${count} because ${SKIP_REASON_MESSAGES[reason]}`,
  );

  return `${reasons.length} transaction${reasons.length === 1 ? ' was' : 's were'} skipped: ${parts.join('; ')}.`;
};

/** Pre-suggestion values for the review dialog (existing → suggested). */
export type ReviewLlmOriginalTxnValues = {
  coa: ChartOfAccountProps | null;
  payee: string | null;
};

const toPlainCoa = (coa: ChartOfAccountProps | null | undefined): ChartOfAccountProps | null => {
  if (!coa?.type || coa.id == null) {
    return null;
  }
  return { type: coa.type, id: coa.id };
};

const getAiCategoryList = () => {
  const state = store?.getState() as RootState;
  const categoryList = getCategoriesById(state).toJS() as LlmToolsCategoryDict;
  const excludeBusinessCategories = selectShouldHideBusinessFromAiChat(state);
  const aiCats = categoriesForAI(categoryList, excludeBusinessCategories);
  log.debug('llmTools/transactions: CatLIst requested ', aiCats);
  return aiCats;
};

export const categoryTools = {
  category_list: {
    definition: {
      name: 'category_list',
      description:
        'When asked to do any categorization, ONLY use categories from this list of categories and their internal ids available to the user. No parameters are required. NEVER show category IDs to the user - always display category names only. Be concise and brief in your response.',
      parameters: {
        type: 'object' as const,
        properties: {},
        required: [],
      },
    },
    handler: (msg: ToolMessage) => {
      const aiCats = getAiCategoryList();
      log.debug('llmTools/transactions: ABOUT TO SEND category_data RESPONSE', aiCats);
      return returnToolResponse(msg, aiCats);
    },
  },

  assign_categories_payees_to_transactions_STEP1: {
    definition: {
      name: 'assign_categories_payees_to_transactions_STEP1',
      description:
        'FIRST STEP to propose category and/or payee updates. NEVER ask clarifying questions, offer options, or suggest batches. NEVER show transaction IDs or category IDs to the user. Be concise and brief in your response.',
      parameters: {
        type: 'object' as const,
        properties: {
          mappings: {
            type: 'array' as const,
            description: 'List of transactions you intend to update (category and/or payee)',
            items: {
              type: 'object' as const,
              properties: {
                id: {
                  type: 'string' as const,
                  description: 'The transaction ID',
                },
                payee: {
                  type: 'string' as const,
                  description: 'The transaction payee',
                },
                date: {
                  type: 'string' as const,
                  description: 'The transaction date',
                },
                category: {
                  type: 'string' as const,
                  description: 'The current category name',
                },
              },
              required: ['id', 'payee', 'date', 'category'],
            },
          },
        },
        required: ['mappings'],
      },
    },
    handler: (msg: ToolMessage) => {
      tracker.track(tracker.events.AIChatBulkEditCategoriesPayees);

      const fnArgs = safeParse(msg.arguments) || {};

      log.debug(
        'llmTools/transactions: RECEIVED Tool Call for assignCategoriesPayeesToTransactionsSTEP1 with args: ',
        msg,
        fnArgs,
      );

      const state = store?.getState() as RootState;
      const limitToolPayloadsEnabled = !!featureFlagsSelectors.getFeatureFlag(state, 'aiChatLimitToolPayloads');
      const transactionCount = fnArgs?.mappings?.length || 0;
      const maxMappings = limitToolPayloadsEnabled ? DEFAULT_MAX_CATEGORIZATION_TRANSACTIONS : transactionCount;
      const exceedsLimit = transactionCount > maxMappings;
      const mappingsToUse = exceedsLimit
        ? (fnArgs.mappings as Step1Mapping[]).slice(0, maxMappings)
        : (fnArgs.mappings as Step1Mapping[]);

      if (exceedsLimit) {
        log.warn('llmTools/transactions: Too many transactions for categorization, using most recent', {
          provided: transactionCount,
          using: maxMappings,
        });
      }

      const cacheKey = msg.callId || `step1_${uuid()}`;
      transactionMappingsCache.set(cacheKey, mappingsToUse);

      const aiCats = getAiCategoryList();
      categoryListCache.set(cacheKey, aiCats);

      log.debug(
        'llmTools/transactions: Cached (categories+payees) mappings and categories with key:',
        cacheKey,
        'Transaction count:',
        fnArgs.mappings?.length,
        'Category count:',
        Object.keys(aiCats).length,
      );

      const limitMessage = exceedsLimit
        ? `Found ${transactionCount} transactions. Working with the most recent ${mappingsToUse.length}. User can narrow down their search if needed.`
        : '';

      const output = `${mappingsToUse.length} transactions ready.${limitMessage ? ` ${limitMessage}` : ''} Categories: ${JSON.stringify(aiCats)}

Analyze each transaction's payee and propose the best category. Different payees typically need different categories.
Show your proposals to the user, then call STEP2 with cacheKey=${cacheKey} and all ${mappingsToUse.length} mappings.`;

      return returnToolResponse(msg, output);
    },
  },

  assign_categories_payees_to_transactions_STEP2: {
    definition: {
      name: 'assign_categories_payees_to_transactions_STEP2',
      description:
        'FINAL STEP for category/payee updates. Only use after STEP1 (assign_categories_payees_to_transactions_STEP1) and user confirmation. Validates mappings, corrects IDs, and displays the UI dialog for review. CRITICAL REQUIREMENTS: 1. You MUST include EVERY transaction from STEP1 - the mappings array length MUST match the count from STEP1. 2. Use the EXACT transaction IDs from STEP1 - do not change, simplify, or renumber them. 3. If changing categories, use the EXACT category IDs from STEP1 - do not change, simplify, or renumber them. 4. Do not skip, filter, or omit any transactions from STEP1. Payee changes are optional. Category changes are optional. At least one of category or payee should be provided for each mapping you intend to update. Split, investment, estimated, and invoice-related transactions are automatically skipped, as are business transactions when business data is not available to chat for this user - the response will explain which ones were skipped and why, so relay that to the user. Be concise and brief in your response.',
      parameters: {
        type: 'object' as const,
        properties: {
          cacheKey: {
            type: 'string' as const,
            description: 'The cache key provided by STEP1 to retrieve original transaction data',
          },
          mappings: {
            type: 'array' as const,
            minItems: 1,
            description:
              "List of transaction-to-category/payee mappings. MUST include ALL transactions from STEP1. Use category names and ids from the 'category_list' tool. Provide payee if you are changing it.",
            items: {
              type: 'object' as const,
              additionalProperties: false,
              properties: {
                id: {
                  type: 'string' as const,
                  description: 'The EXACT transaction ID from STEP1 - do not change this ID',
                },
                categoryName: {
                  type: 'string' as const,
                  description: 'The category name (optional if not changing category)',
                },
                categoryId: {
                  type: 'string' as const,
                  description: 'The category ID (optional if not changing category)',
                },
                payee: {
                  type: 'string' as const,
                  description: 'The new payee name (optional)',
                },
              },
              required: ['id'],
            },
          },
        },
        required: ['mappings', 'cacheKey'],
      },
    },
    handler: (msg: ToolMessage) => {
      log.debug('llmTools/transactions: STEP2 (categories+payees) Handler called');
      const fnArgs = safeParse(msg.arguments) || {};
      const state = store?.getState() as RootState;

      log.debug(
        'llmTools/transactions: RECEIVED Tool Call for assignCategoriesPayeesToTransactionsSTEP2 with args: ',
        msg,
        fnArgs,
      );

      const cacheKey = fnArgs?.cacheKey;
      const originalTransactions = cacheKey ? (transactionMappingsCache.get(cacheKey) as Step1Mapping[]) : null;
      const cachedCategoryList = cacheKey ? categoryListCache.get(cacheKey) : null;

      if (!originalTransactions) {
        const errorMsg = 'ERROR: No cached transaction data found. Please call STEP1 again.';
        log.error('llmTools/transactions:', errorMsg);
        return returnToolResponse(msg, errorMsg);
      }

      if (!cachedCategoryList) {
        const errorMsg = 'ERROR: No cached category data found. Please call STEP1 again.';
        log.error('llmTools/transactions:', errorMsg);
        return returnToolResponse(msg, errorMsg);
      }

      log.debug('llmTools/transactions: Found cached transactions:', originalTransactions.length);
      log.debug('llmTools/transactions: Found cached categories:', Object.keys(cachedCategoryList).length);
      log.debug('llmTools/transactions: Received mappings:', fnArgs.mappings?.length);

      if (fnArgs.mappings.length !== originalTransactions.length) {
        const errorMsg = `ERROR: Transaction count mismatch. STEP1 had ${originalTransactions.length} transactions, but STEP2 received ${fnArgs.mappings.length} mappings. You MUST update ALL ${originalTransactions.length} transactions. Please try again and include all transactions.`;
        log.error('llmTools/transactions:', errorMsg);
        return returnToolResponse(msg, errorMsg);
      }

      const findCategoryIdByName = (categoryName: string | undefined): string | null => {
        const normalizedName = categoryName?.toLowerCase().trim();
        for (const cat of cachedCategoryList) {
          if (cat.name?.toLowerCase().trim() === normalizedName) {
            return cat.id;
          }
        }
        return null;
      };

      const updatedMappings: Step2Mapping[] = fnArgs.mappings.map((mapping: Step2Mapping, index: number) => {
        const originalTxn = originalTransactions[index];
        if (!originalTxn) {
          return mapping;
        }

        const restoredTxnId = originalTxn.id;
        log.debug(`llmTools/transactions: Mapping position ${index}: txn ${mapping.id} -> ${restoredTxnId}`);

        let correctedCategoryId = mapping.categoryId;
        const correctCategoryId = mapping.categoryName ? findCategoryIdByName(mapping.categoryName) : null;

        if (correctCategoryId && correctCategoryId !== mapping.categoryId) {
          log.debug(
            `llmTools/transactions: Correcting category ID for "${mapping.categoryName}": ${mapping.categoryId} -> ${correctCategoryId}`,
          );
          correctedCategoryId = correctCategoryId;
        } else if (mapping.categoryName && !correctCategoryId && mapping.categoryId) {
          log.warn(
            `llmTools/transactions: Could not find category ID for name "${mapping.categoryName}", using provided ID: ${mapping.categoryId}`,
          );
        }

        return {
          ...mapping,
          id: restoredTxnId,
          categoryId: correctedCategoryId,
          originalPayee: originalTxn.payee,
        };
      });
      fnArgs.mappings = updatedMappings;

      const txnIdSet = new Set<string>(fnArgs?.mappings.map((item: Step2Mapping) => item.id) || []);
      log.debug('llmTools/transactions: Transaction IDs to find:', txnIdSet.size);

      const allTxns = transactionsSelectors.getResourcesById(state);
      log.debug('llmTools/transactions: All transactions count:', allTxns.size);

      const excludeBusinessTxns = selectShouldHideBusinessFromAiChat(state);
      const businessCoaIds = getBusinessCoaIdsSet(state);

      const skipReasons: SkipReason[] = [];
      const matchedIds = new Set<string>();

      const txnList = allTxns.filter((txn) => {
        if (!txn.id || !txnIdSet.has(txn.id)) return false;
        matchedIds.add(txn.id);
        if (isInvestmentTransaction(txn)) {
          skipReasons.push('investment');
          return false;
        }
        if (isEstimatedTransaction(txn)) {
          skipReasons.push('estimated');
          return false;
        }
        if (isInvoiceRelatedTransaction(txn)) {
          skipReasons.push('invoice');
          return false;
        }
        if (isSplitTransaction(txn)) {
          skipReasons.push('split');
          return false;
        }
        if (excludeBusinessTxns && isBusinessTransaction(txn, businessCoaIds)) {
          skipReasons.push('business');
          return false;
        }
        return true;
      });
      txnIdSet.forEach((id) => {
        if (!matchedIds.has(id)) skipReasons.push('not_found');
      });
      log.debug('llmTools/transactions: Found transactions:', txnList.size, txnList.toJS());

      const skippedSummary = describeSkippedTransactions(skipReasons);

      if (txnList.size === 0) {
        const errorMsg = skippedSummary
          ? `ERROR: No transactions could be updated. ${skippedSummary}`
          : 'ERROR: No transactions found. The transaction IDs provided do not match any transactions in the system. ' +
            'This likely means the IDs were changed between STEP1 and STEP2. ' +
            'Please call STEP1 again and use the EXACT transaction IDs provided in that step.';
        log.error('llmTools/transactions:', errorMsg);
        return returnToolResponse(msg, errorMsg);
      }

      if (txnList.size < txnIdSet.size) {
        log.warn(
          `llmTools/transactions: Only found ${txnList.size} transactions out of ${txnIdSet.size} requested. ${skippedSummary}`,
        );
      }

      const mappingsById = new Map<string, Step2Mapping>(fnArgs.mappings.map((m: Step2Mapping) => [m.id, m]));

      // Preserve pre-suggestion coa/payee so the review dialog can show existing → suggested.
      const originalByTxnId: Record<string, ReviewLlmOriginalTxnValues> = {};
      txnList.forEach((txn) => {
        if (!txn.id) {
          return;
        }
        originalByTxnId[txn.id] = {
          coa: toPlainCoa(txn.coa),
          payee: txn.payee ?? null,
        };
      });

      const txnListUpdated = txnList.map((txn) => {
        const mapping = txn.id ? mappingsById.get(txn.id) : null;
        if (mapping) {
          let updatedTxn = txn;
          if (mapping.categoryId) {
            updatedTxn = updatedTxn.set('coa', { type: 'CATEGORY', id: mapping.categoryId }).merge(
              resolveBusinessFieldsForCategory(state, {
                categoryId: mapping.categoryId,
                accountId: txn.accountId,
                current: txn,
              }),
            );
          }
          if (mapping.payee) {
            updatedTxn = updatedTxn.set('payee', mapping.payee);
          }
          return updatedTxn;
        }
        return txn;
      });

      log.debug('llmTools/transactions: Transactions list to be updated: ', txnListUpdated.toJS());

      transactionMappingsCache.delete(cacheKey);
      categoryListCache.delete(cacheKey);
      log.debug('llmTools/transactions: Cleared cache entries:', cacheKey);

      return new Promise((resolve) => {
        const callback = (result: {
          success: boolean;
          updatedCount?: number;
          errors?: string[];
          warnings?: string[];
        }) => {
          if (result.success) {
            const message = `Successfully updated ${
              result.updatedCount
            } transaction${result.updatedCount === 1 ? '' : 's'}.${skippedSummary ? ` ${skippedSummary}` : ''}${
              result.warnings && result.warnings.length > 0 ? ` Note: ${result.warnings.join('; ')}` : ''
            }`;
            log.debug('llmTools/transactions: Updates successful:', message);
            resolve(returnToolResponse(msg, message));
          } else {
            const errorMessage = `Failed to update transactions. Errors: ${
              result.errors?.join('; ') || 'Unknown error'
            }`;
            log.error('llmTools/transactions: Updates failed:', errorMessage);
            resolve(returnToolResponse(msg, errorMessage));
          }
        };

        const dialogData = mkRootUiData({
          type: 'LLM_CATEGORIZE_TRANSACTIONS',
          id: `llm-categorize-payees-${uuid()}`,
          visible: true,
          allowNesting: false,
          props: ImmutableMap({
            transactions: txnListUpdated.valueSeq().toArray(),
            originalByTxnId,
            callback,
          }),
        });
        log.debug('Dialog data (categories+payees):', dialogData.toJS());
        store?.dispatch(createDialog(dialogData));
        log.debug('Dialog dispatched successfully');
      });
    },
  },
};
